import { IColumn, TooltipHost } from '@fluentui/react';
import { FC, useMemo } from 'react';
import { TimeStamp } from '../../models/reports.internal';
import { HDAgentSegment, useGetCompletedConversationsQuery } from '../../redux/apis/historicalDataApi';
import { getTimestamp } from '../../utils/timeUtils';
import usePagedListReducer from '../../shared/hooks/usePagedListReducer';
import useTimeFormatters from '../../shared/hooks/useTimeFormatters';
import PagedDetailsList from './PagedDetailsList';
import { TooltipWrapper } from './TooltipWrapper';

interface IProps {
    queueId: string;
    startOfToday: TimeStamp | null;
    abandonedThreshold?: number | null;
}

const CompletedConversations: FC<IProps> = ({ queueId, startOfToday }) => {
    const [{ page, pageSize, sortingColumn, isSortedDescending }, dispatch] = usePagedListReducer({ sortingColumn: 'createdAt' });
    const { formatDateTime, formatTimeSpan } = useTimeFormatters();

    const lastMidnight = useMemo(
        () => startOfToday ? new Date(startOfToday * 1000).toISOString() : '',
        [startOfToday],
    );

    const { data: results, isLoading } = useGetCompletedConversationsQuery(
        {
            queueId,
            from: lastMidnight ?? '',
            skip: page * pageSize,
            take: pageSize,
            orderBy: `${sortingColumn} ${isSortedDescending ? 'desc' : 'asc'}`,
        },
        {
            skip: !lastMidnight,
            refetchOnMountOrArgChange: true,
        },
    );

    const columns = useMemo(
        (): IColumn[] => [
            {
                key: 'timestamp',
                name: 'Timestamp',
                minWidth: 150,
                maxWidth: 150,
                onRender: ({ createdAtUtc }: HDAgentSegment) => <TooltipWrapper text={formatDateTime(getTimestamp(createdAtUtc))} />,
                onColumnClick: () => dispatch({ type: 'ApplySorting', value: 'createdAt' }),
                isSorted: sortingColumn === 'createdAt',
                isSortedDescending,
            },
            {
                key: 'totalTime',
                name: 'Total time',
                minWidth: 80,
                maxWidth: 80,
                onRender: ({ conversation }: HDAgentSegment) => {
                    const time = formatTimeSpan(conversation.durationInSeconds);
                    return <TooltipHost content={time}>{time}</TooltipHost>;
                },
                onColumnClick: () => dispatch({ type: 'ApplySorting', value: 'conversation/durationInSeconds' }),
                isSorted: sortingColumn === 'conversation/durationInSeconds',
                isSortedDescending,
            },
            {
                key: 'connectedTime',
                name: 'Connected time',
                minWidth: 125,
                maxWidth: 125,
                onRender: (segment: HDAgentSegment) => {
                    const time = formatTimeSpan(segment.durationInSeconds);
                    return <TooltipHost content={time}>{time}</TooltipHost>;
                },
                onColumnClick: () => dispatch({ type: 'ApplySorting', value: 'durationInSeconds' }),
                isSorted: sortingColumn === 'durationInSeconds',
                isSortedDescending,
            },
            {
                key: 'waitTime',
                name: 'Wait time',
                minWidth: 90,
                maxWidth: 90,
                onRender: ({ queueSegment }: HDAgentSegment) => {
                    const time = formatTimeSpan(queueSegment.durationInSeconds);
                    return <TooltipHost content={time}>{time}</TooltipHost>;
                },
                onColumnClick: () => dispatch({ type: 'ApplySorting', value: 'queueSegment/durationInSeconds' }),
                isSorted: sortingColumn === 'queueSegment/durationInSeconds',
                isSortedDescending,
            },
            {
                key: 'selfServiceTime',
                name: 'Self-Service time',
                minWidth: 110,
                maxWidth: 110,
                onRender: (segment: HDAgentSegment) => {
                    const remaining = segment.conversation.durationInSeconds - segment.durationInSeconds - segment.queueSegment.durationInSeconds;
                    const time = formatTimeSpan(Math.max(0, remaining));
                    return <TooltipHost content={time}>{time}</TooltipHost>;
                },
            },
            {
                key: 'customerName',
                name: 'Customer name',
                minWidth: 150,
                onRender: ({ conversation }: HDAgentSegment) => {
                    const name = [conversation.customerFirstName, conversation.customerMiddleName, conversation.customerLastName].filter(n => n !== '').join(' ') || 'Unknown';
                    return <TooltipHost content={name}>{name}</TooltipHost>;
                },
            },
            {
                key: 'customerIdentifier',
                name: 'Customer identifier',
                minWidth: 150,
                maxWidth: 150,
                onRender: ({ conversation }: HDAgentSegment) => {
                    const identifier = conversation.customerPhoneNumber || conversation.customerEmailAddress || '';
                    return <TooltipHost content={identifier}>{identifier}</TooltipHost>;
                },
            },
            {
                key: 'type',
                name: 'Type',
                minWidth: 100,
                maxWidth: 100,
                onRender: ({ conversation }: HDAgentSegment) => {
                    const type = conversation.virtualConversationType ?? 'Inbound';
                    return <TooltipHost content={type}>{type}</TooltipHost>;
                },
                onColumnClick: () => dispatch({ type: 'ApplySorting', value: 'conversation/virtualConversationType' }),
                isSorted: sortingColumn === 'conversation/virtualConversationType',
                isSortedDescending,
            },
        ],
        [dispatch, formatDateTime, formatTimeSpan, isSortedDescending, sortingColumn],
    );

    const completedConversations = useMemo<HDAgentSegment[]>(
        () => results?.agentSegments ?? [],
        [results],
    );

    const total = results?.total ?? 0;

    return (
        <PagedDetailsList
            total={total}
            items={completedConversations}
            columns={columns}
            isLoading={isLoading}

            pageIndex={page}
            onPrevPageClick={() => dispatch({ type: 'PrevPage' })}
            onNextPageClick={() => dispatch({ type: 'NextPage' })}

            allowSearch={false}
            allowFilter={false}
            allowPageSizeChange={true}
            onPageSizeChange={(value) => dispatch({ type: 'SetPageSize', value })}

            styles={{ root: { minWidth: '960px !important', height: '600px !important' } }}
        />
    );
};
export default CompletedConversations;